'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { ServicesMap } from './ServicesMap'
import type { ServiceMapPin } from './ServicesMap'
import { ServiceListRow } from './ServiceListRow'
import { ServiceCard } from './ServiceCard'
import type { CategoryData, ServiceCardData, ClusterOption } from './types'

const PER_CATEGORY_PREVIEW = 6

export function ServicesPageClient({
  categories,
  services,
  featured,
  clusters,
  initialCategory,
}: {
  categories: CategoryData[]
  services: ServiceCardData[]
  featured: ServiceCardData[]
  clusters: ClusterOption[]
  initialCategory?: string
}) {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState(initialCategory ?? '')
  const [cluster, setCluster] = useState('')
  const [view, setView] = useState<'list' | 'map'>('list')
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)

  const filtersActive = search.trim() !== '' || category !== '' || cluster !== ''

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return services.filter(s => {
      if (q && !s.name.toLowerCase().includes(q)) return false
      if (category && s.categorySlug !== category) return false
      if (cluster && s.clusterSlug !== cluster) return false
      return true
    })
  }, [services, search, category, cluster])

  const clearFilters = () => {
    setSearch('')
    setCategory('')
    setCluster('')
  }

  const mapPins: ServiceMapPin[] = useMemo(() => (
    filtered
      .filter(s => s.lat != null && s.lng != null)
      .map(s => ({
        slug: s.slug,
        name: s.name,
        lat: s.lat!,
        lng: s.lng!,
        categorySlug: s.categorySlug,
        categoryIcon: s.categoryIcon,
        categoryColor: s.categoryColor,
      }))
  ), [filtered])

  return (
    <div className="svc-page">
      {/* Category navigation */}
      <nav className="svc-cat-nav" aria-label="Service categories">
        <button
          type="button"
          className={`svc-cat-pill${category === '' ? ' svc-cat-pill--active' : ''}`}
          onClick={() => setCategory('')}
        >
          All
        </button>
        {categories.map(c => (
          <button
            key={c.slug}
            type="button"
            className={`svc-cat-pill${category === c.slug ? ' svc-cat-pill--active' : ''}`}
            onClick={() => setCategory(category === c.slug ? '' : c.slug)}
          >
            <span className="svc-cat-pill-icon">{c.icon}</span>
            {c.name}
            <span className="svc-cat-pill-count">{c.count}</span>
          </button>
        ))}
      </nav>

      {/* Featured / useful services */}
      {featured.length > 0 && (
        <section className="svc-featured">
          <h2 className="svc-section-title">Featured &amp; Useful for Visitors</h2>
          <div className="svc-featured-track">
            {featured.map(s => <ServiceCard key={s.id} service={s} />)}
          </div>
        </section>
      )}

      {/* Filter bar */}
      <div className="svc-filter-bar">
        <input
          type="text"
          className="svc-search"
          placeholder="Search services by name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="svc-select" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => (
            <option key={c.slug} value={c.slug}>{c.name}</option>
          ))}
        </select>
        <select className="svc-select" value={cluster} onChange={e => setCluster(e.target.value)}>
          <option value="">All locations</option>
          {clusters.map(cl => (
            <option key={cl.slug} value={cl.slug}>{cl.name}</option>
          ))}
        </select>
        <div className="svc-view-toggle">
          <button
            type="button"
            className={`svc-view-btn${view === 'list' ? ' svc-view-btn--active' : ''}`}
            onClick={() => setView('list')}
          >
            ≡ List
          </button>
          <button
            type="button"
            className={`svc-view-btn${view === 'map' ? ' svc-view-btn--active' : ''}`}
            onClick={() => setView('map')}
          >
            ⌖ Map
          </button>
        </div>
        {filtersActive && (
          <button type="button" className="svc-clear-filters" onClick={clearFilters}>
            Clear filters ✕
          </button>
        )}
      </div>

      {view === 'map' ? (
        <div className="svc-map-split">
          <div className="svc-map-split-map">
            <ServicesMap pins={mapPins} selectedSlug={selectedSlug} onMarkerClick={setSelectedSlug} />
          </div>
          <div className="svc-map-split-list">
            {filtered.length > 0 ? (
              filtered.map(s => (
                <ServiceListRow
                  key={s.id}
                  service={s}
                  selected={s.slug === selectedSlug}
                  onSelect={setSelectedSlug}
                />
              ))
            ) : (
              <p className="svc-empty">No services match your search.</p>
            )}
          </div>
        </div>
      ) : filtersActive ? (
        <section className="svc-results">
          {filtered.length > 0 ? (
            <div className="svc-grid">
              {filtered.map(s => <ServiceCard key={s.id} service={s} />)}
            </div>
          ) : (
            <p className="svc-empty">No services match your search.</p>
          )}
        </section>
      ) : (
        categories.map(c => {
          const items = services.filter(s => s.categorySlug === c.slug).slice(0, PER_CATEGORY_PREVIEW)
          return (
            <section className="svc-cat-section" id={`cat-${c.slug}`} key={c.slug}>
              <div className="svc-section-head">
                <h2 className="svc-section-title">
                  <span className="svc-section-icon">{c.icon}</span> {c.name}
                </h2>
                {c.count > PER_CATEGORY_PREVIEW && (
                  <Link href={`/services?category=${c.slug}`} className="svc-viewall">View all →</Link>
                )}
              </div>
              <div className="svc-grid">
                {items.map(s => <ServiceCard key={s.id} service={s} />)}
              </div>
            </section>
          )
        })
      )}
    </div>
  )
}
